import type { Guide } from "./types.js";
import { Lead, H2, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "gsc-search-console",
  title: "Google Search Console — ranking data per article",
  category: "Reference",
  lastUpdated: "2026-06-10",
  description: "Pulls 28-day per-page performance (impressions, clicks, CTR, average position, top queries) into the Content DB. Free, nightly, no per-call cost.",
  relatedGuides: ["content-db", "credentials-reference", "error-log"],
  readTimeMin: 6,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Once configured, every live <AppLink to="content-db">Content DB</AppLink> row gets real GSC performance attached: how many impressions and clicks each AlternaBe article got in the last 28 days, its average rank, and the top search queries it shows up for. Nightly sync. $0 per call.
      </Lead>

      <H2>What gets pulled per article</H2>
      <Ul>
        <Li><strong>Impressions (28d)</strong> — how many times the URL appeared in Google search results.</Li>
        <Li><strong>Clicks (28d)</strong> — how many of those impressions led to a click.</Li>
        <Li><strong>CTR (28d)</strong> — clicks / impressions. Low CTR + high rank = title/description weakness.</Li>
        <Li><strong>Average position (28d)</strong> — impression-weighted mean rank. Lower is better (1 = top of page 1).</Li>
        <Li><strong>Top 10 queries</strong> — the actual search queries the URL ranked for, each with its own impressions/clicks/position. Click a row's "queries" cell to see the breakdown.</Li>
        <Li><strong>gscLastSyncedAt</strong> — surfaced in the UI as "synced Nd ago"; rows older than 2 days get a stale-data warning.</Li>
      </Ul>

      <H2>Why this matters</H2>
      <Ul>
        <Li><strong>Content gaps</strong> — articles ranking position 5-15 are one optimisation pass away from page 1. Refresh those, not the ones already at rank 1.</Li>
        <Li><strong>Title/meta-description weakness</strong> — high impressions + low CTR = your title or description isn't winning the click. Easy fix.</Li>
        <Li><strong>Topic discovery</strong> — the top-queries list shows what real users actually search for. Often surprising vs the keyword you produced the article around.</Li>
        <Li><strong>Cluster-level intelligence</strong> — sum up impressions per cluster to see real ranking strength. Feeds the eventual Entity Hub's share-of-voice metric.</Li>
      </Ul>

      <H2>Setup (~5 minutes, one-time)</H2>
      <Ul>
        <Li>
          Open <AppLink to="credentials">Credentials</AppLink> → "Google Search Console" card.
        </Li>
        <Li>
          Click "Setup instructions". Three sections:
          <ol className="my-2 list-decimal pl-6 text-sm">
            <li>Enable the Search Console API on your existing Cloud project (the same one you used for Drive, if you've set Drive up).</li>
            <li>One-time refresh-token OAuth via the Playground. Scope: <C>https://www.googleapis.com/auth/webmasters.readonly</C>.</li>
            <li>Paste the GSC property URL exactly as it appears in Search Console (URL-prefix form <C>https://www.alternabe.co.il/</C> OR domain form <C>sc-domain:alternabe.co.il</C>).</li>
          </ol>
        </Li>
        <Li>
          Click Test. Green = "Authenticated · &lt;siteUrl&gt; (siteFullUser)". If red, the test surfaces which sites the user CAN see, so you know whether the issue is "wrong account" or "wrong site URL".
        </Li>
        <Li>
          Edit <C>.env</C>: flip <C>GSC_AUTORUN=off</C> to <C>on</C>. Restart ui-server. The next 04:00 IL cron tick runs the sync.
        </Li>
      </Ul>

      <Callout kind="tip">
        Don't wait for 04:00 — hit "Sync GSC now" on the Content DB page after first config. Fills the columns immediately so you can sanity-check the numbers.
      </Callout>

      <H2>Cron + manual trigger</H2>
      <Ul>
        <Li><strong>Schedule</strong> — 04:00 IL daily (<C>GSC_SYNC_CRON</C>). Disabled by default until you configure credentials (so we don't spam errors at 04:00 every night for fresh clones).</Li>
        <Li><strong>Manual trigger UI</strong> — "Sync GSC now" button on the Content DB page. Shows progress + per-URL result counts.</Li>
        <Li><strong>Manual trigger API</strong> — <C>POST /api/published-articles/gsc/sync</C> with optional <C>&#123; onlySanityId &#125;</C> body to scope to a single article.</Li>
        <Li><strong>Cost</strong> — $0 (GSC API is free, quota is 1200 req/min — far above our ~110 live URLs).</Li>
        <Li><strong>Latency</strong> — ~25-30s for a full sync (~200ms per URL × 110 articles). One API call per URL because GSC's <C>searchAnalytics.query</C> filters per page; we need per-URL aggregates AND per-URL top-queries.</Li>
      </Ul>

      <H2>Data freshness</H2>
      <Ul>
        <Li>GSC reports data with ~2 day lag. The sync uses <C>endDate = yesterday</C>, <C>startDate = 28 days before that</C> → today's row reflects activity from ~30 days ago to 2 days ago.</Li>
        <Li>If a row's <C>gscLastSyncedAt</C> is more than 2 days old, the Content DB shows a stale-warning pill. Cause is usually the cron tripped on bad credentials — check the <AppLink to="errors">Errors page</AppLink> for <C>scheduler.gsc-sync</C> entries.</Li>
        <Li>Newly-published articles need at least 2-3 days before GSC has any data on them. Brand-new rows will show as "no data" for a while; that's expected.</Li>
      </Ul>

      <H2>Failure modes</H2>
      <Ul>
        <Li>
          <strong>"Refresh-token exchange failed"</strong> — refresh token was revoked (most commonly because the OAuth consent screen was modified or the OAuth client deleted). Re-do the Playground flow + paste a new refresh token.
        </Li>
        <Li>
          <strong>"Authenticated user has no access to &lt;siteUrl&gt;"</strong> — the Google account you OAuth'd with isn't a Search Console user for the property. Either add that user in GSC's Settings → Users & permissions, or OAuth with a different account.
        </Li>
        <Li>
          <strong>Some URLs return "no data"</strong> — that URL hasn't had any impressions in the 28-day window. Genuinely zero, not an error. Common for fresh articles or archived pages.
        </Li>
        <Li>
          <strong>URL mismatch</strong> — your local <C>published_articles.url</C> field doesn't match the actual ranking URL on Google. Fix via the <C>url_override</C> column on the Content DB row.
        </Li>
      </Ul>

      <H2>What's not here yet</H2>
      <Ul>
        <Li><strong>Historical trend</strong> — we only keep the current 28-day window. Long-term trend tracking is the Entity Hub's job (parked; see <C>docs/future/entity-intelligence-hub.md</C>).</Li>
        <Li><strong>Cluster aggregates</strong> — share of voice per cluster (sum of cluster URLs' impressions / total cluster keyword impression budget). Useful but builds on top of the per-URL data we just landed.</Li>
        <Li><strong>Auto-refresh suggestions</strong> — "this article is at rank 8 with high impressions → re-produce it" as a Task. Easy to add once the data is flowing.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
